import { type JSX, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ColumnsIcon } from '@dynatrace/strato-icons';

import type { DataTableColumnSettingsTriggerProps } from './column-settings-types.js';
import { Button } from '../../../../../buttons/button/Button.js';
import { Tooltip } from '../../../../../overlays/tooltip/Tooltip.js';
import {
  useDataTable,
  useDataTableColumnSettings,
} from '../../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../../messages.js';

interface ColumnSettingsProps {
  customTriggerProps?: DataTableColumnSettingsTriggerProps;
}

/**
 * DataTable column settings. Internally used component containing a trigger button
 * that opens the column settings modal which displays a list of all columns.
 * @internal
 */
export const DataTableColumnSettings = (
  props: ColumnSettingsProps,
): JSX.Element => {
  const { customTriggerProps } = props;
  const {
    children: customTrigger,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  } = customTriggerProps ?? {};

  const intl = useIntl();
  const table = useDataTable();
  const columnSettingsModal = useDataTableColumnSettings();

  const columnSettingsButton = useMemo(() => {
    const hiddenColumnCount = table
      .getAllLeafColumnsWithoutBuiltin()
      .filter((column) => !column.getIsVisible()).length;

    const message =
      hiddenColumnCount === 0
        ? intl.formatMessage(dataTableMessages.columnSettingsButtonDefault)
        : intl.formatMessage(dataTableMessages.columnSettingsButtonVisibility, {
            hiddenColumnCount,
          });

    return {
      text: message,
      hiddenColumnCount,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.store.state.columnVisibility]);

  return (
    <Tooltip text={intl.formatMessage(dataTableMessages.columnSettingsTooltip)}>
      <Button
        onClick={columnSettingsModal?.openColumnSettings}
        variant="default"
        className={consumerClassName}
        style={consumerStyle}
        data-testid={dataTestId}
        {...remainingProps}
      >
        <Button.Prefix>
          <ColumnsIcon />
        </Button.Prefix>
        {customTrigger
          ? customTrigger({
              hiddenColumnCount: columnSettingsButton.hiddenColumnCount,
              totalColumnCount: table.getAllColumns().length,
              defaultLabel: columnSettingsButton.text,
            })
          : columnSettingsButton.text}
      </Button>
    </Tooltip>
  );
};
